package dev.danko.roblox_craft_ai.uikit.components

import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.compose.ui.styleModifier
import org.jetbrains.compose.web.css.FlexWrap
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.foundation.layout.Row
import androidx.compose.runtime.remember
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.text.SpanText
import dev.danko.roblox_craft_ai.shared.animation.AnimationSetup
import dev.danko.roblox_craft_ai.shared.animation.CombatMode
import dev.danko.roblox_craft_ai.shared.animation.Grip
import dev.danko.roblox_craft_ai.shared.animation.Hand
import dev.danko.roblox_craft_ai.shared.animation.WeaponKind
import dev.danko.roblox_craft_ai.uikit.i18n.strings
import dev.danko.roblox_craft_ai.uikit.icons.AppIcon
import dev.danko.roblox_craft_ai.uikit.theme.LocalAppTheme
import kotlinx.browser.localStorage
import org.jetbrains.compose.web.css.px

/** The animation settings of this browser: stance, weapons and grip, sent with every new animation. */
object AnimationSetupPrefs {
    private const val KEY = "rc.anim.setup"

    var setup by mutableStateOf(read())
        private set

    fun update(change: (AnimationSetup) -> AnimationSetup) {
        setup = change(setup)
        try { localStorage.setItem(KEY, setup.toMetadata().entries.joinToString("|") { "${it.key}=${it.value}" }) } catch (_: Throwable) {}
    }

    private fun read(): AnimationSetup = try {
        val raw = localStorage.getItem(KEY).orEmpty()
        AnimationSetup.fromMetadata(raw.split('|').mapNotNull { part -> part.split('=', limit = 2).takeIf { it.size == 2 }?.let { it[0] to it[1] } }.toMap())
    } catch (_: Throwable) {
        AnimationSetup()
    }
}

/**
 * Panel of the animation tab: the chosen character (picked in the model window from [library]), combat mode, the
 * weapon of each hand (built-in or the user's models), grip and the hand holding a single weapon.
 */
@Composable
fun AnimationSetupPicker(
    enabled: Boolean = true,
    library: ModelLibrary? = null,
    /** Opens the model window (drawn by the page, outside the prompt box): true starts on the weapon. */
    onOpenLibrary: ((weapon: Boolean) -> Unit)? = null
) {
    val a = strings().animationSetup
    val palette = LocalAppTheme.current.colors
    val setup = AnimationSetupPrefs.setup

    Column(Modifier.fillMaxWidth().gap(10.px)) {
        // Own characters: 3D works that got bones in the viewer's tools («Поставить кости»).
        val models = library?.models.orEmpty()
        val character = models.firstOrNull { it.riggedUrl != null && it.riggedUrl == setup.characterUrl }
        val armed = setup.mode == CombatMode.ONE_WEAPON || setup.mode == CombatMode.TWO_WEAPONS
        // The chosen models in short; the model window does the choosing, uploading and fixing.
        Row(Modifier.fillMaxWidth().gap(8.px).flexWrap(FlexWrap.Wrap), verticalAlignment = Alignment.CenterVertically) {
            ChosenModel(a.characterTitle, character?.title ?: a.characterR15, character?.image, AppIcon.FIGURE, enabled && onOpenLibrary != null) { onOpenLibrary?.invoke(false) }
            if (onOpenLibrary != null) {
                AppButton(text = a.libraryOpen, icon = AppIcon.FOLDER, variant = AppButtonVariant.OUTLINE, size = AppButtonSize.SM, enabled = enabled,
                    onClick = { onOpenLibrary(armed && setup.characterUrl != null) })
            }
        }
        // The mode, then for each hand the weapon (built-in or the user's own models), grip and hand.
        IconChoices(
            a.modeTitle,
            listOf(
                IconOption(CombatMode.NONE, AppIcon.FIGURE, a.modeNone, a.modeNoneHint),
                IconOption(CombatMode.FISTS, AppIcon.FIST, a.modeFists, a.modeFistsHint),
                IconOption(CombatMode.ONE_WEAPON, AppIcon.SWORD, a.modeOne, a.modeOneHint),
                IconOption(CombatMode.TWO_WEAPONS, AppIcon.SWORDS, a.modeTwo, a.modeTwoHint)
            ),
            setup.mode,
            enabled
        ) { mode -> AnimationSetupPrefs.update { it.copy(mode = mode) } }
        if (setup.mode == CombatMode.ONE_WEAPON) {
            WeaponPicker(a.weaponTitleOne, models, setup.weapon, setup.weaponModelUrl, enabled = enabled) { choice ->
                AnimationSetupPrefs.update { it.copy(weapon = choice.kind, weaponModelUrl = choice.model?.glbUrl, weaponName = choice.model?.title) }
            }
            Row(Modifier.fillMaxWidth().gap(14.px).flexWrap(FlexWrap.Wrap)) {
                IconChoices(
                    a.gripTitle,
                    listOf(IconOption(Grip.ONE_HAND, AppIcon.HAND, a.gripOne, a.gripOneHint), IconOption(Grip.TWO_HANDS, AppIcon.HANDS, a.gripTwo, a.gripTwoHint)),
                    setup.grip,
                    enabled
                ) { grip -> AnimationSetupPrefs.update { it.copy(grip = grip) } }
                IconChoices(
                    if (setup.grip == Grip.TWO_HANDS) a.handLeadTitle else a.handTitle,
                    listOf(IconOption(Hand.RIGHT, AppIcon.HAND, a.handRight, a.handRightHint), IconOption(Hand.LEFT, AppIcon.HAND_LEFT, a.handLeft, a.handLeftHint)),
                    setup.hand,
                    enabled
                ) { hand -> AnimationSetupPrefs.update { it.copy(hand = hand) } }
            }
        }
        if (setup.mode == CombatMode.TWO_WEAPONS) {
            WeaponPicker(a.rightHandTitle, models, setup.weapon, setup.weaponModelUrl, enabled = enabled) { choice ->
                AnimationSetupPrefs.update { it.copy(weapon = choice.kind, weaponModelUrl = choice.model?.glbUrl, weaponName = choice.model?.title) }
            }
            WeaponPicker(a.leftHandTitle, models, setup.offhand, setup.offhandModelUrl, allowStaff = false, enabled = enabled) { choice ->
                AnimationSetupPrefs.update { it.copy(offhand = choice.kind, offhandModelUrl = choice.model?.glbUrl, offhandName = choice.model?.title) }
            }
        }
        if (setup.mode != CombatMode.NONE) {
            SpanText(if (setup.mode == CombatMode.FISTS) a.note else a.noteWithWeapon, Modifier.fontSize(12.px).color(palette.textSecondary))
        }
    }
}

/** One chosen model in the animation form: a small picture and its name; a click opens the model window. */
@Composable
private fun ChosenModel(label: String, name: String, image: String?, icon: AppIcon, enabled: Boolean, onClick: () -> Unit) {
    val palette = LocalAppTheme.current.colors
    org.jetbrains.compose.web.dom.Button(attrs = Modifier.padding(4.px, 10.px, 4.px, 4.px).gap(8.px).borderRadius(10.px)
        .display(org.jetbrains.compose.web.css.DisplayStyle.Flex).alignItems(com.varabyte.kobweb.compose.css.AlignItems.Center)
        .border(1.px, org.jetbrains.compose.web.css.LineStyle.Solid, palette.borderStrong).backgroundColor(palette.background)
        .color(palette.textPrimary).cursor(if (enabled) com.varabyte.kobweb.compose.css.Cursor.Pointer else com.varabyte.kobweb.compose.css.Cursor.Default)
        .styleModifier { property("font-family", "inherit") }
        .toAttrs {
            attr("type", "button")
            attr("title", "$label: $name")
            if (!enabled) attr("disabled", "")
            onClick { onClick() }
        }) {
        com.varabyte.kobweb.compose.foundation.layout.Box(Modifier.size(36.px).borderRadius(7.px).backgroundColor(palette.surface)
            .styleModifier { property("overflow", "hidden") }, contentAlignment = Alignment.Center) {
            if (image != null) org.jetbrains.compose.web.dom.Img(src = dev.danko.roblox_craft_ai.uikit.config.BackendEndpoint.resolveAssetUrl(image),
                attrs = Modifier.fillMaxWidth().fillMaxHeight().objectFit(com.varabyte.kobweb.compose.css.ObjectFit.Cover).toAttrs { attr("alt", "") })
            else com.varabyte.kobweb.compose.foundation.layout.Box(Modifier.color(palette.textSecondary)) { dev.danko.roblox_craft_ai.uikit.icons.Icon(icon, size = 18) }
        }
        Column(Modifier.gap(0.px)) {
            SpanText(label, Modifier.fontSize(11.px).color(palette.textSecondary))
            SpanText(name, Modifier.fontSize(13.px).fontWeight(com.varabyte.kobweb.compose.css.FontWeight.SemiBold).maxWidth(150.px)
                .styleModifier { property("overflow", "hidden"); property("text-overflow", "ellipsis"); property("white-space", "nowrap") })
        }
    }
}
